import React, { useState } from 'react'
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import styles from "../../styles/styles";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { server } from "../../server";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { loadUser } from "../../redux/actions/user";


const Login = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("")
    const [visible, setVisible] = useState(false)
    const [isReinstating, setIsReinstating] = useState(false);
    const [showReinstateModal, setShowReinstateModal] = useState(false);

    // Get redirect parameter from URL
    const searchParams = new URLSearchParams(window.location.search);
    const redirectPath = searchParams.get('redirect') || "/";

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // Try unified login endpoint (works for both users and sellers)
            const res = await axios.post(
                `${server}/user/login-user`,
                {
                    email,
                    password,
                },
                { withCredentials: true }
            );
            
            if (res.data.success) {
                toast.success("Login Success!");
                // Load user into Redux state
                await dispatch(loadUser());
                // Navigate to intended page or home
                navigate(redirectPath, { replace: true });
            }
        } catch (err) {
            const errorCode = err?.response?.data?.code;
            if (errorCode === "ACCOUNT_DELETED") {
                setShowReinstateModal(true);
                return;
            }
            if (errorCode === "EMAIL_PERMANENTLY_BLOCKED") {
                toast.error(
                    err?.response?.data?.message ||
                        "This email has been permanently removed and cannot be used on this site."
                );
                return;
            }
            // Try seller login endpoint as fallback (for backward compatibility)
            try {
                const sellerRes = await axios.post(
                    `${server}/shop/login-shop`,
                    {
                        email,
                        password,
                    },
                    { withCredentials: true }
                );
                
                if (sellerRes.data.success) {
                    toast.success("Login Success!");
                    await dispatch(loadUser());
                    // Navigate to intended page or home
                    navigate(redirectPath, { replace: true });
                }
            } catch (sellerErr) {
                toast.error(err?.response?.data?.message || err?.message || "Login failed");
            }
        }
    };

    const handleReinstate = async () => {
        try {
            setIsReinstating(true);
            const reinstateRes = await axios.post(
                `${server}/user/reinstate-account`,
                { email, password },
                { withCredentials: true }
            );
            if (reinstateRes.data?.success) {
                toast.success("Your original account has been reinstated.");
                setShowReinstateModal(false);
                await dispatch(loadUser());
                navigate(redirectPath, { replace: true });
            }
        } catch (reinstateErr) {
            toast.error(reinstateErr?.response?.data?.message || "Failed to reinstate account.");
        } finally {
            setIsReinstating(false);
        }
    };

    return (
        <div className='flex flex-col justify-center py-12 sm:px-6 lg:px-8'>
            {showReinstateModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 p-8 relative">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                                <svg
                                    className="h-8 w-8 text-red-600"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                    />
                                </svg>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                Reinstate Account
                            </h2>
                            <p className="text-gray-600 mb-6">
                                This account was previously deleted. Would you like to reinstate your original account using these credentials?
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={() => {
                                        setShowReinstateModal(false);
                                        toast.info("Use a different email if you want to create a new account.");
                                    }}
                                    disabled={isReinstating}
                                    className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-md font-semibold hover:bg-gray-50 transition-colors disabled:opacity-60"
                                >
                                    Use different email
                                </button>
                                <button
                                    onClick={handleReinstate}
                                    disabled={isReinstating}
                                    className="flex-1 px-6 py-3 bg-[#38513b] text-white rounded-md font-semibold hover:bg-[#2f4232] transition-colors disabled:opacity-60"
                                >
                                    {isReinstating ? "Reinstating..." : "Reinstate account"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className='sm:mx-auto sm:w-full sm:max-w-md'>
                <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                    Login to your account
                </h2>
            </div>
            <div className='mt-8 sm:mx-auto sm:w-full sm:max-w-md'>
                <div className='bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10'>
                    <form className='space-y-6' onSubmit={handleSubmit} >
                        {/* Email */}
                        <div>
                            <label htmlFor="email"
                                className='block text-sm font-medium text-gray-700'
                            >
                                Email address
                            </label>
                            <div className='mt-1'>
                                <input type="email"
                                    name='email'
                                    autoComplete='email'
                                    required
                                    placeholder='Please enter valid email'
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className='appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#CCBEA1] focus:border-[#CCBEA1] sm:text-sm'
                                />

                            </div>
                        </div>
                        {/* Password */}
                        <div>
                            <label htmlFor="password"
                                className='block text-sm font-medium text-gray-700'
                            >
                                password
                            </label>
                            <div className='mt-1 relative'>
                                <input type={visible ? "text" : "password"}
                                    name='password'
                                    autoComplete='password'
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className='appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#CCBEA1] focus:border-[#CCBEA1] sm:text-sm'
                                />
                                {visible ? (
                                    <AiOutlineEye
                                        className="absolute right-2 top-2 cursor-pointer"
                                        size={25}
                                        onClick={() => setVisible(false)}
                                    />
                                ) : (
                                    <AiOutlineEyeInvisible
                                        className="absolute right-2 top-2 cursor-pointer"
                                        size={25}
                                        onClick={() => setVisible(true)}
                                    />
                                )}

                            </div>
                        </div>
                        {/* password end */}

                        <div className={`${styles.noramlFlex} justify-between`}>
                            <div className={`${styles.noramlFlex}`}>
                                <input
                                    type="checkbox"
                                    name="remember-me"
                                    id="remember-me"
                                    className="h-4 w-4 text-[#CCBEA1] focus:ring-[#CCBEA1] border-gray-300 rounded"
                                />
                                <label
                                    htmlFor="remember-me"
                                    className="ml-2 block text-sm text-gray-900"
                                >
                                    Remember me
                                </label>
                            </div>
                            <div className='text-sm'>
                                <Link
                                    to="/forgot-password"
                                    className="font-medium text-[#CCBEA1] hover:text-[#CCBEA1]"
                                >
                                    Forgot your password?
                                </Link>
                            </div>
                        </div>
                        <div>
                            <button
                                type='submit'
                                disabled={isReinstating}
                                className=' className="group relative w-full h-[40px] flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-black bg-[#CCBEA1] hover:bg-[#CCBEA1]"'
                            >
                                {isReinstating ? "Reinstating..." : "Submit"}
                            </button>
                        </div>

                        <div className={`${styles.noramlFlex} w-full justify-between`} >
                            <h4>Don't have an account?</h4>
                            <Link to="/sign-up" className="text-[#CCBEA1] pl-2">
                                Sign Up
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default Login